import { Failure } from '@/usecases/repository';
import { handleApiError, handleNetworkError } from '@/services/errorHandler';
import { AppConfig } from './AppConfig';
import { decryptToken } from '@/utils/tokenCrypto';

/**
 * Codifica cada segmento de um path REST para evitar path traversal.
 * Ex: `v1/ouvidorias/${id}/agentes` → cada parte é encodeURIComponent'd.
 */
function encodePathSegments(endpoint: string): string {
  return endpoint.split('/').map((segment) => encodeURIComponent(segment)).join('/');
}

/**
 * Obtém ou gera o token CSRF para double-submit cookie.
 */
function getCsrfToken(): string {
  const key = 'csrfToken';
  let token = '';
  try {
    token = localStorage.getItem(key) || '';
  } catch { /* cookies indisponíveis */ }
  if (!token) {
    token = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`;
    try {
      localStorage.setItem(key, token);
    } catch { /* noop */ }
  }
  return token;
}

interface Token {
  accessToken: string;
  payload?: Record<string, unknown>;
}

interface ProblemDetails {
  type: string;
  title: string;
  detail?: string;
  status: number;
  instance?: string;
  traceId: string;
  errors?: Record<string, unknown>;
}

interface ApiResponse<T> {
  statusCode: number;
  body: unknown;
  data?: T;
}

class Settings {
  // private static readonly users: Record<string, Token> = {
  //   "Thomas": {
  //     accessToken: 'eyJhbGciOiJQUzI1NiIsImtpZCI6IjE0WWlfREVmZHhFWTl6SnlQS1dvU2ciLCJ0eXAiOiJKV1QifQ.eyJzdWIiOiI0NzkiLCJyb2xlIjpbIkNvbnN1bWlkb3IiLCJBZG1pbmlzdHJhZG9yIl0sImp0aSI6IjJiZTk1NTFiLTExYmUtNGRkZC1hMDdjLWNlNzlmYmVmMGIwZiIsIm5iZiI6MTc3Njk2OTA4OCwiaWF0IjoxNzc2OTY5MDg4LCJlbWFpbF92ZXJpZmllZCI6dHJ1ZSwiY2xpZW50X2lkIjoiNjRhMDczYjMtNTk2Ny00YTIyLTIxNTYtMmYxNTUyYTg0OTEyIiwidGlkIjo0NjEsImNpZCI6IjE0MmQzMGI5LTRjY2QtNGI1Zi1hZWI1LTI1YTc2NWUxNTVmMCIsImV4cCI6MTc3OTU2MTA4OCwiaXNzIjoiaHR0cHM6Ly9hcGkudG9vbGJveC5hcHAuYnIifQ.CkQ88HgRvoI3Ub6TrZ4VncblZ1rhNnwGo55ere9MEcsvg9IVpJ4Fi7Pz1qHnyDhARoVUjhIgAzUHZS2MxWky5V7DDr9N1qXoC0oIOGi0_OJ6o_pm42AwyrXwjvywVGVDFd0qPRb8gDz8dmWEOOItQ0qvNIYuX5o--kCPvjZU3X5aeNyJpI9JLt18Y8wy4itgYgmk9eTrGq7XgherOFr4C6K9GlUXAMmV9sp5rT9CVJiFujZ-dAOpOKXxdxGV_6ovgk92DBu7-b_nWqUc0iA2kRe6tLRDIK-DuO5WE5oH3q6eeU7-Nuvw-H1fyIYkJF4YUsAVtB91_z3C4xfuIyg6fMLysRJCP1tDnaKN3SjDwIcxv9emQFZi04Dl871HBfwLPCVmWepOsmwk71n57FgKG5wgCEwZ5vRWOvNrRTnkqVRSGCWEZBo4yVNBbEZVkyQ7A6jBW6eunEY0WfZrpM8jcGBHOx6aQ47ZGE_CnmblHvcSl1-FG6lzNme3G7OQ72E8',
  //   },
  // };

  static readonly urlBase = "https://api.toolbox.app.br/urbnet";
  static readonly urlSecondary = "https://api.toolbox.app.br/autenticacao";
  static readonly urlAuth = "https://api.toolbox.app.br";
  static token: Token = AppConfig.token;
}

class RemoteDataSource {
  private readonly urlBase: string;

  constructor(urlBase: string) {
    this.urlBase = urlBase;
  }

  async get<TData>({
    endpoint,
    requiredToken = true,
    headers,
    serialize,
    queryParams,
    forceRefresh,
  }: {
    endpoint: string;
    requiredToken?: boolean;
    headers?: Record<string, string>;
    serialize?: (data: unknown) => TData;
    queryParams?: Record<string, unknown>;
    forceRefresh: boolean;
  }): Promise<{ success: ApiResponse<TData> } | { failure: Failure }> {
    const uri = this.buildUrl({
      endpoint,
      queryParams,
    });

    // Adiciona timestamp para evitar cache quando forceRefresh é true
    if (forceRefresh) {
      uri.searchParams.append('_t', Date.now().toString());
    }

    const token = await this.getToken(requiredToken);
    const requestHeaders = this.buildHeaders({ token, includeContentType: false });
    
    // Headers para evitar cache do navegador
    requestHeaders['Cache-Control'] = 'no-cache, no-store, must-revalidate';
    requestHeaders['Pragma'] = 'no-cache';
    requestHeaders['Expires'] = '0';
    
    if (headers) {
      Object.assign(requestHeaders, headers);
    }

    try {
      const response = await fetch(uri.toString(), {
        method: 'GET',
        headers: requestHeaders,
        cache: forceRefresh ? 'no-store' : 'default',
      });

      if (this.wasSuccessful(response)) {
        // Handle 204 No Content - don't try to parse JSON
        const responseMap = response.status === 204 
          ? {} 
          : await response.json().catch(() => ({}));

        const apiResponse: ApiResponse<TData> = {
          statusCode: response.status,
          body: responseMap,
          data: serialize ? serialize(responseMap) : undefined,
        };
        return { success: apiResponse };
      }

      const failure = await this.handleApiFailure(response);
      return { failure };
    } catch (error) {
      const failure = this.handleNetworkFailure(error);
      return { failure };
    }
  }

  async post<TData>({
    endpoint,
    requiredToken = true,
    headers,
    serialize,
    body,
    queryParams,
  }: {
    endpoint: string;
    requiredToken?: boolean;
    headers?: Record<string, string>;
    serialize?: (data: unknown) => TData;
    body?: Record<string, unknown> | FormData;
    queryParams?: Record<string, unknown>;
  }): Promise<{ success: ApiResponse<TData> } | { failure: Failure }> {
    const uri = this.buildUrl({
      endpoint,
      queryParams,
    });

    const token = await this.getToken(requiredToken);
    const isFormData = body instanceof FormData;
    const requestHeaders = this.buildHeaders({ token, includeContentType: !isFormData });
    if (headers) {
      Object.assign(requestHeaders, headers);
    }

    const requestBody = isFormData ? body : JSON.stringify(body);

    try {
      const response = await fetch(uri.toString(), {
        method: 'POST',
        headers: requestHeaders,
        body: requestBody as BodyInit,
      });

      if (this.wasSuccessful(response)) {
        // Handle 204 No Content - don't try to parse JSON
        const responseMap = response.status === 204 
          ? {} 
          : await response.json().catch(() => ({}));

        const apiResponse: ApiResponse<TData> = {
          statusCode: response.status,
          body: responseMap,
          data: serialize ? serialize(responseMap) : undefined,
        };
        return { success: apiResponse };
      }

      const failure = await this.handleApiFailure(response);
      return { failure };
    } catch (error) {
      const failure = this.handleNetworkFailure(error);
      return { failure };
    }
  }

  async put<TData>({
    endpoint,
    requiredToken = true,
    headers,
    serialize,
    body,
  }: {
    endpoint: string;
    requiredToken?: boolean;
    headers?: Record<string, string>;
    serialize?: (data: unknown) => TData;
    body?: Record<string, unknown>;
  }): Promise<{ success: ApiResponse<TData> } | { failure: Failure }> {
    const uri = this.buildUrl({
      endpoint,
    });

    const token = await this.getToken(requiredToken);
    const requestHeaders = this.buildHeaders({ token });
    if (headers) {
      Object.assign(requestHeaders, headers);
    }

    const requestBody = JSON.stringify(body);

    try {
      const response = await fetch(uri.toString(), {
        method: 'PUT',
        headers: requestHeaders,
        body: requestBody,
      });

      if (this.wasSuccessful(response)) {
        // Handle 204 No Content - don't try to parse JSON
        const responseMap = response.status === 204 
          ? {} 
          : await response.json().catch(() => ({}));

        const apiResponse: ApiResponse<TData> = {
          statusCode: response.status,
          body: responseMap,
          data: serialize ? serialize(responseMap) : undefined,
        };
        return { success: apiResponse };
      }

      const failure = await this.handleApiFailure(response);
      return { failure };
    } catch (error) {
      const failure = this.handleNetworkFailure(error);
      return { failure };
    }
  }

  async delete({
    endpoint,
    requiredToken = true,
    headers,
    body,
  }: {
    endpoint: string;
    requiredToken?: boolean;
    headers?: Record<string, string>;
    body?: Record<string, unknown>;
  }): Promise<{ success: ApiResponse<boolean> } | { failure: Failure }> {
    const uri = this.buildUrl({
      endpoint,
    });

    const token = await this.getToken(requiredToken);
    const requestHeaders = this.buildHeaders({ token });
    if (headers) {
      Object.assign(requestHeaders, headers);
    }

    const requestBody = JSON.stringify(body);

    try {
      const response = await fetch(uri.toString(), {
        method: 'DELETE',
        headers: requestHeaders,
        body: requestBody,
      });

      if (this.wasSuccessful(response)) {
        const apiResponse: ApiResponse<boolean> = {
          statusCode: response.status,
          body: null,
          data: response.status === 204,
        };
        return { success: apiResponse };
      }

      const failure = await this.handleApiFailure(response);
      return { failure };
    } catch (error) {
      const failure = this.handleNetworkFailure(error);
      return { failure };
    }
  }

  async patch<TData>({
    endpoint,
    requiredToken = true,
    headers,
    serialize,
    body,
  }: {
    endpoint: string;
    requiredToken?: boolean;
    headers?: Record<string, string>;
    serialize?: (data: unknown) => TData;
    body?: Record<string, unknown> | unknown[];
  }): Promise<{ success: ApiResponse<TData> } | { failure: Failure }> {
    const uri = this.buildUrl({
      endpoint,
    });

    const token = await this.getToken(requiredToken);
    const requestHeaders = this.buildHeaders({ token });
    // Use JSON content type for PATCH (server now expects application/json)
    requestHeaders["content-type"] = "application/json";
    if (headers) {
      Object.assign(requestHeaders, headers);
    }

    const requestBody = JSON.stringify(body);

    try {
      const response = await fetch(uri.toString(), {
        method: 'PATCH',
        headers: requestHeaders,
        body: requestBody,
      });

      if (this.wasSuccessful(response)) {
        // Handle 204 No Content - don't try to parse JSON
        const responseMap = response.status === 204
          ? {}
          : await response.json().catch(() => ({}));

        const apiResponse: ApiResponse<TData> = {
          statusCode: response.status,
          body: responseMap,
          data: serialize ? serialize(responseMap) : undefined,
        };
        return { success: apiResponse };
      }

      const failure = await this.handleApiFailure(response);
      return { failure };
    } catch (error) {
      const failure = this.handleNetworkFailure(error);
      return { failure };
    }
  }

  private async handleApiFailure(response: Response): Promise<Failure> {
    let responseBody: unknown;
    try {
      responseBody = await response.json();
    } catch {
      responseBody = undefined;
    }

    const apiError = handleApiError(response.status, responseBody);
    
    return new Failure({ 
      messages: [apiError.details || apiError.message],
    });
  }

  private handleNetworkFailure(error: unknown): Failure {
    const apiError = handleNetworkError(error);
    
    return new Failure({ 
      messages: [apiError.message],
    });
  }

  map<TData>(response: { success: ApiResponse<TData> } | { failure: Failure }): { success: TData } | { failure: Failure } {
    if ('success' in response) {
      if (response.success.data !== undefined) {
        return { success: response.success.data };
      }
      return { failure: new Failure({ messages: ['No data returned'] }) };
    }
    return response;
  }

  private async getToken(requiredToken: boolean): Promise<Token | undefined> {
    if (!requiredToken) return undefined;

    // 1) Token em memória (AppConfig é a fonte de verdade)
    if (AppConfig.tokenAcesso) {
      Settings.token = { accessToken: AppConfig.tokenAcesso };
      return Settings.token;
    }

    if (Settings.token?.accessToken) {
      AppConfig.tokenAcesso = Settings.token.accessToken;
      AppConfig.token = Settings.token;
      return Settings.token;
    }

    // 2) Fallback: recupera o token encriptado do cookieStorage (ex: após reload da página)
    const encrypted = typeof document !== 'undefined' ? localStorage.getItem('tokenAcesso') : null;
    if (encrypted) {
      const accessToken = await decryptToken(encrypted);
      if (accessToken) {
        AppConfig.tokenAcesso = accessToken;
        AppConfig.token = { accessToken };
        Settings.token = AppConfig.token;
        return Settings.token;
      }
    }

    return undefined;
  }

  private buildUrl({
    endpoint,
    queryParams,
    preferredUrl,
  }: {
    endpoint: string;
    queryParams?: Record<string, unknown>;
    preferredUrl?: string;
  }): URL {
    const apiUri = preferredUrl 
      ? new URL(preferredUrl)
      : new URL(this.urlBase);

    // Separa path e query string caso o endpoint já venha com parâmetros
    // (ex.: "solicitacoes?agenteId=...&ouvidoriaId=...").
    const [endpointPath, endpointQuery] = endpoint.split("?");
    const safeEndpoint = encodePathSegments(endpointPath);
    const apiPath = `${apiUri.pathname}/${safeEndpoint}`;

    const uri = new URL(apiPath, apiUri.origin);

    // Preserva parâmetros que já vieram dentro do endpoint
    if (endpointQuery) {
      const params = new URLSearchParams(endpointQuery);
      params.forEach((value, key) => {
        if (!uri.searchParams.has(key)) {
          uri.searchParams.append(key, value);
        }
      });
    }
    
    if (queryParams) {
      Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          uri.searchParams.append(key, String(value));
        }
      });
    }

    return uri;
  }

  private buildHeaders({ token, includeContentType = true }: { token?: Token; includeContentType?: boolean }): Record<string, string> {
    const headers: Record<string, string> = {};
    if (includeContentType) {
      headers["content-type"] = "application/json";
    }
    headers["client-id"] = AppConfig.appId;
    headers["x-csrf-token"] = getCsrfToken();
    if (token != null) {
      headers["authorization"] = `bearer ${token.accessToken}`;
    }
    return headers;
  }

  private wasSuccessful(response: Response): boolean {
    return response.status >= 200 && response.status < 300;
  }
}

export { RemoteDataSource, Settings };
  export type { Token, ProblemDetails, ApiResponse };
export type { Failure };
